import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import {
  GuardsCheckEnd,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  ResolveStart,
  RouteConfigLoadEnd,
  RouteConfigLoadStart,
  Router,
  RouterEvent,
  RouterOutlet,
} from '@angular/router'

import { TranslateService } from '@ngx-translate/core'
import { PrimeNG } from 'primeng/config'
import { Skeleton } from 'primeng/skeleton'
import { Subscription } from 'rxjs'
import { filter, take } from 'rxjs/operators'

import { LanguageService } from '@ti/core/ui-kit/i18n'
import { AppUpdateComponent } from '@ti/core/ui-kit/layout/components/app-update'
import { applyPrimeNgOverrides } from '@ti/core/ui-kit/prime-ng/overrides'

import { isMcpLandingPath } from './initial-loader'

// TODO: Implement better way to apply overrides
applyPrimeNgOverrides()

@Component({
  selector: 'app-tsp-admin-root',
  templateUrl: 'tsp-admin-app.component.html',
  styleUrls: ['tsp-admin-add.component.scss'],
  imports: [Skeleton, RouterOutlet, AppUpdateComponent],
})
export class TspAdminAppComponent implements OnInit {
  protected readonly loading = signal(false)
  private readonly router = inject(Router)
  private readonly translate = inject(TranslateService)
  private readonly languageService = inject(LanguageService)
  private readonly primeNgConfig = inject(PrimeNG)
  private readonly destroyRef = inject(DestroyRef)
  // we only show the loader during this phase (when user ope n app); the consent page has its own
  private initialPhase = !this.router.navigated && !isMcpLandingPath(window.location.pathname)

  // track lazy chunk downloads during the initial navigation
  private lazyLoadCount = 0
  private navigationInterceptorSub?: Subscription

  ngOnInit() {
    // if initial navigation hasn't happened yet, show the skeleton right away
    if (this.initialPhase) {
      this.loading.set(true)

      // turn it off after the FIRST navigation finishes (or is cancelled/errored)
      this.router.events
        .pipe(
          filter(
            (e): e is NavigationEnd | NavigationCancel | NavigationError =>
              e instanceof NavigationEnd ||
              e instanceof NavigationCancel ||
              e instanceof NavigationError,
          ),
          take(1),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe(() => {
          // if lazy modules are still loading, keep it on until they finish
          if (this.lazyLoadCount === 0) {
            this.finishInitialPhase()
          }
          // else we'll finish when the last RouteConfigLoadEnd arrives
        })
    }

    // Interceptor for initial phase only.
    // IMPORTANT: allow lazy-load events through (they are NOT RouterEvent).
    if (this.initialPhase) {
      this.navigationInterceptorSub = this.router.events
        .pipe(
          filter(
            (e) =>
              e instanceof RouterEvent || // NavigationStart/End/etc.
              e instanceof RouteConfigLoadStart ||
              e instanceof RouteConfigLoadEnd,
          ),
        )
        .subscribe((e) => this.navigationInterceptor(e as any))
    }
  }
  private finishInitialPhase() {
    this.loading.set(false)
    this.initialPhase = false
    this.navigationInterceptorSub?.unsubscribe()
    this.navigationInterceptorSub = undefined
  }

  private navigationInterceptor(event: RouterEvent): void {
    if (!this.initialPhase) {
      // ignore all events after app is loaded and unsubscribe
      this.loading.set(false)
      this.navigationInterceptorSub?.unsubscribe()
      this.navigationInterceptorSub = undefined
      return
    }

    // keep loader visible while initial lazy modules download
    if (event instanceof RouteConfigLoadStart) {
      this.lazyLoadCount++
      this.loading.set(true)
    }
    if (event instanceof RouteConfigLoadEnd) {
      this.lazyLoadCount = Math.max(0, this.lazyLoadCount - 1)
      if (this.lazyLoadCount === 0 && this.router.navigated) {
        this.loading.set(false)
        this.initialPhase = false
      }
    }

    // these are not strictly necessary for the initial-phase-only case,
    // but harmless: they ensure the loader is on once activation is confirmed
    if (event instanceof GuardsCheckEnd && event.shouldActivate) {
      this.loading.set(true)
    }
    if (event instanceof ResolveStart) {
      this.loading.set(true)
    }
  }
}
